import { DropdownButton, MenuItem } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';
import Tooltip from '../../../generic/Tooltip';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import { window } from '../../../../globals';
import useSelectRoles from './hooks/useSelectRoles';
import type { SelectOption } from '../../../../types';
import styles from './TeamSettings.module.less';

//  Trying to keep window references outside of the component whenever possible.
const getCurrentUserId = () => window.user?.id;

type ComponentProps = {
  /** The ID of the user to select the role for */
  userId: number;
  /** If it's disabled, this is due to permission issues */
  isDisabled: boolean;
  /** If it's the current user, we need to show a different tooltip */
  isCurrentUser: boolean;
  /** The role options to display in the dropdown */
  roleOptions: Array<SelectOption<number>>;
  /** The currently selected role */
  selectedRole: SelectOption<number> | null;
  /** The function to call when the role changes */
  onChangeRole: (role: SelectOption<number>) => void;
};

/**
 * This is a dumb component which renders a styled DropdownButton, allowing
 * the user to select the role for a given user on a team/in a team group.
 */
const TeamRoleSelector: VoidFunctionComponent<ComponentProps> = ({
  userId,
  isDisabled,
  isCurrentUser,
  roleOptions,
  selectedRole,
  onChangeRole,
}) => {
  //  Note we call .toString() here mostly for type safety, since technically SelectOption['label] could be a ReactNode.
  const selectedRoleLabel = selectedRole?.label?.toString() ?? 'None';
  if (isDisabled) {
    const tooltipMessage = `You are not permitted to ${isCurrentUser ? 'change your own role in a team.' : 'manage users on this team.'}`;
    return (
      <Tooltip id={`team-role-select-tooltip-${userId}`} tooltip={tooltipMessage}>
        <span className="inline-block">
          {selectedRoleLabel}
        </span>
      </Tooltip>
    );
  }

  return (
    <DropdownButton
      id={`team-role-select-dropdown-${userId}`}
      className={styles.teamRoleSelector}
      title={selectedRoleLabel}
      bsStyle="link"
    >
      {roleOptions.map(({ value: roleId, label }) => (
        <MenuItem
          key={roleId}
          active={roleId === selectedRole?.value}
          onSelect={() => onChangeRole({ value: roleId, label })}
        >
          {label}
        </MenuItem>
      ))}
    </DropdownButton>
  );
};

type ContainerProps = {
  /** ID of the user to select the role for */
  userId: number;
  /** The current team/team group ID. Used for permission checking. */
  teamId: number;
  /** The initial/currently selected role ID. Optional because it might not be set yet. */
  roleId?: number;
  /**
   * The function to call when the role changes.
   * @param {number} userId - The ID of the user whose role is being changed
   * @param {number} roleId - The ID of the new role being assigned
   * @returns {Promise<void> | void} A promise that resolves when the role change is complete
   */
  handleChangeRole: (userId: number, roleId: number) => Promise<void> | void;
};

/**
 * Container component which connects the TeamRoleSelector up with
 * some state, specifically it pulls in the Roles options and determines
 * if the current user is allowed to manage the given user's role.
 */
const TeamRoleSelectorContainer: VoidFunctionComponent<ContainerProps> = ({
  userId,
  roleId,
  teamId,
  handleChangeRole,
}) => {
  //  Permissions checks
  const hasPermission = useHasPermission();
  const isCurrentUser = userId === getCurrentUserId();
  const canManageRoles = hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });
  const canEditTeamUsers =
    hasPermission({ resourceType: ResourceType.TeamUserAny, action: Action.Edit }) ||
    hasPermission({ resourceType: ResourceType.TeamUser, action: Action.Edit, teamId });
  const isDisabled = (isCurrentUser && !canManageRoles) || !canEditTeamUsers;

  //  Role options
  const {
    roleOptions,
    isLoading,
    selectedRole,
    onChangeRole,
  } = useSelectRoles(roleId);

  const onRoleChange = (role: SelectOption<number>) => {
    //  Update the local state, so that the UI updates immediately.
    onChangeRole(role);
    //  Call the parent function to update the team/role in the database.
    handleChangeRole(userId, role.value);
  };

  return (
    <TeamRoleSelector
      userId={userId}
      isDisabled={isLoading || isDisabled}
      isCurrentUser={isCurrentUser}
      roleOptions={roleOptions}
      selectedRole={selectedRole}
      onChangeRole={onRoleChange}
    />
  );
};

export default TeamRoleSelectorContainer;
